<template>
    <div class="info-column">
        <div
            v-gtm:type="'PrizePool'"
            v-gtm:page="gtmPage"
            v-gtm:label="gtmLabel"
            v-gtm:@click
            class="info-title"
            v-on:click="expandBlock()"
        >
            <img
                v-bind:src="jackpotPoolIconUrl"
                width="28"
                height="28"
                alt=""
                aria-hidden="true"
            />
            <div class="info-group">
                <div class="info-text">
                    <div class="type">
                        {{ jackpotPoolTypeText }}
                    </div>
                    <div class="amount">
                        <Odometer
                            v-bind:value="pool.poolAmount"
                        />
                    </div>
                </div>
                <SvgIcon
                    v-if="isShowArrow"
                    name="icon-system-arrow"
                    v-bind:rotated="!isExpanded"
                />
            </div>
        </div>
        <div class="info-contentGroup">
            <div
                v-show="isExpanded"
                class="info-content"
            >
                <div class="section-title">
                    {{ i18n.t('jackpot_min_qualifying_bet') }}
                </div>
                <div v-if="isNotMiniPool" class="section-detail">
                    {{ alsoQualifyText }}
                </div>
                <div class="section-amount">
                    {{ minQualifyingAmountText }}
                </div>
            </div>
            <div
                v-if="pool.lastWinner"
                v-show="isExpanded"
                class="info-content"
            >
                <div class="section-title">
                    {{ i18n.t('jackpot_last_winner') }}
                </div>
                <div v-if="lastTriggeredTimeText" class="section-detail">
                    {{ lastTriggeredTimeText }} <span class="timeZone-text">GMT-4</span>
                </div>
                <div class="section-detail">
                    {{ pool.lastWinner }}
                </div>
                <div v-if="lastWinAmountText" class="section-amount">
                    {{ lastWinAmountText }}
                </div>
            </div>
            <div
                v-show="isExpanded"
                class="info-content"
            >
                <div class="section-title">
                    {{ i18n.t('jackpot_number_of_winners') }}
                </div>
                <div class="section-amount">
                    {{ totalNumberOfWinner }}
                </div>
            </div>
            <div
                v-show="isExpanded"
                v-if="pool.lastWinner"
                class="info-content"
            >
                <div class="section-title">
                    {{ i18n.t('jackpot_total_won') }}
                </div>
                <div class="section-amount">
                    {{ totalWonAmountText }}
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { type PropType, computed } from 'vue';
    import Odometer from '@/components/jackpot/Odometer.vue';
    import { formatToServerTime } from '@/core/lib/date';
    import { i18n } from '@/core/lib/i18n';
    import { SiteStyle } from '@/interface/enum';
    import { JackpotPoolType, type PoolSummary } from '@/interface/jackpot';
    import { useCustomerStore } from '@/store/customerStore';
    import { useToggleStore } from '@/store/toggleStore';

    const props = defineProps({
        isExpanded: {
            type: Boolean,
            default: false,
        },
        isShowArrow: {
            type: Boolean,
            default: true,
        },
        pool: {
            type: Object as PropType<PoolSummary>,
            required: true,
        },
    });

    const { formatMoneyWithSymbolAndDecimal } = useCustomerStore();
    const { isJackpotEFootballInBigEventTheme, isSwitchEFootballToSimulationSportsZoneEnabled } = storeToRefs(useToggleStore(SiteStyle.Asia));

    const jackpotIcons = import.meta.glob<string>('@/assets/images/jackpot/*.svg', {
        eager: true,
        import: 'default',
        query: '?url',
    });

    const jackpotPoolIconUrl = computed(() => {
        const suffix = props.pool.poolType.toLowerCase();
        const fileName = isJackpotEFootballInBigEventTheme.value
            ? `jackpot-${suffix}-bigEvent`
            : `jackpot-${suffix}`;
        return jackpotIcons[`/src/assets/images/jackpot/${fileName}.svg`] ?? '';
    });

    if (isJackpotEFootballInBigEventTheme.value) {
        import('./JackpotPoolContentBigEvent.scss');
    } else {
        import('./JackpotPoolContent.scss');
    }

    const totalNumberOfWinner = computed(() => {
        if (props.pool.totalNumberOfWinner > 0) {
            return props.pool.totalNumberOfWinner;
        }
        return i18n.t('jackpot_no_winners');
    });

    const emit = defineEmits(['toggle-expand']);

    const expandBlock = () => {
        emit('toggle-expand');
    };

    const jackpotPoolTypeText = computed(() => i18n.t(`jackpot_pool_${props.pool.poolType.toLowerCase()}`));

    const isNotMiniPool = computed(() => props.pool.poolType !== JackpotPoolType.Mini);

    const alsoQualifyText = computed(() => i18n.t(`jackpot_also_qualify_${props.pool.poolType.toLowerCase()}`));

    const minQualifyingAmountText = computed(() => formatMoneyWithSymbolAndDecimal(props.pool.minQualifyingAmount));

    const lastTriggeredTimeText = computed(() => (props.pool.lastTriggeredTime ? `${formatToServerTime(props.pool.lastTriggeredTime, 'MM/dd/yyyy HH:mm:ss')}` : ''));

    const lastWinAmountText = computed(() => (props.pool.lastWonAmount ? formatMoneyWithSymbolAndDecimal(props.pool.lastWonAmount) : ''));

    const totalWonAmountText = computed(() => formatMoneyWithSymbolAndDecimal(props.pool.totalWonAmount));

    const gtmLabel = computed(() => (props.isExpanded ? 'Off' : 'On'));
    const gtmPage = computed(() => (isSwitchEFootballToSimulationSportsZoneEnabled.value ? 'K_JackPot' : 'K_Efootball_JP'));
</script>
